import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { useInventory } from '@features/inventory';
import { useSales } from '@features/sales/hooks/useSales';
import { ProductSales } from '@domain/sales/models/Sales';
import { ProductInventory } from '@domain/inventory/models/Inventory';
import { useGenericDrawer } from '@shared/hooks';
import ProductDetailsDrawer from '@features/products/components/detail/ProductDetailsDrawer';
import { APP_CONFIG } from '../../../../config/app.config';
import DashboardCardHeader from '../general/DashboardCardHeader';
import ProductTable from './ProductTable';
import {
  DEFAULT_PERIOD,
  ForecastPeriod,
  PAGE_SIZE,
  getForecastPeriodRange,
} from './utils';

const DashboardProductTable: React.FC = () => {
  const colors = APP_CONFIG.ui.theme.colors;
  const { openDrawer } = useGenericDrawer();
  const [period, setPeriod] = useState<ForecastPeriod>(DEFAULT_PERIOD);
  const [page, setPage] = useState(1);

  const today = useMemo(() => new Date(), []);
  const periodRange = useMemo(
    () => getForecastPeriodRange(period, today),
    [period, today]
  );

  const { inventory, isLoading } = useInventory({
    page,
    limit: PAGE_SIZE,
    purchasable: true,
  });

  const rows = useMemo(() => {
    if (inventory?.status !== 'success') return [];
    return inventory.data ?? [];
  }, [inventory]);

  const productIds = useMemo(
    () => rows.map((row) => row.product.id).filter(Boolean),
    [rows]
  );

  const { sales } = useSales({
    startDate: periodRange.start,
    endDate: periodRange.end,
    filterCriteria: {
      productIds,
      page: 1,
      limit: Math.max(productIds.length, 1),
      purchasable: true,
      active: true,
    },
    enabled: periodRange.showActuals && productIds.length > 0,
  });

  const salesByProductId = useMemo(() => {
    const map = new Map<string, ProductSales>();
    if (sales?.status !== 'success' || !sales.data) return map;
    for (const item of sales.data) {
      map.set(item.product.id, item);
    }
    return map;
  }, [sales]);

  const totalPages =
    inventory?.status === 'success' ? (inventory.totalPages ?? 0) : 0;

  const openProduct = (item: ProductInventory) => {
    openDrawer(
      'Product Details',
      <ProductDetailsDrawer productId={item.product.id} />
    );
  };

  return (
    <Box
      sx={{
        bgcolor: colors.background,
        border: `1px solid ${colors.border}`,
        borderRadius: '16px',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <DashboardCardHeader title="Products" />


      <ProductTable
        rows={rows}
        isLoading={isLoading}
        page={page}
        totalPages={totalPages}
        period={period}
        periodRange={periodRange}
        today={today}
        salesByProductId={salesByProductId}
        onPeriodChange={setPeriod}
        onPageChange={setPage}
        onOpen={openProduct}
      />
    </Box>
  );
};

export default DashboardProductTable;
